import type { RouteLocationNormalized } from 'vue-router'

import type { Menu } from '@/stores/interface/securityStore.d'
import { openRouteNames } from '@/routers/route'
import { useSecurityStore } from '@/stores/securityStore'

/**
 * Route meta interface
 */
interface RouteMeta {
  protected?: boolean
  [key: string]: unknown
}

/**
 * Route interface for security checks
 */
interface Route {
  name?: string | symbol | null
  meta?: RouteMeta
  [key: string]: unknown
}

/**
 * Recursively check if a route name exists in menu structure
 * @param routeName - The route name to search for
 * @param menu - The menu object to search in
 * @returns True if route name is found, false otherwise
 */
const hasMenuRecursive = (routeName: string | symbol | null | undefined, menu: Menu): boolean => {
  if (routeName === menu.name) return true

  if (menu.menus && menu.menus.length > 0) {
    for (const subMenu of menu.menus) {
      const result = hasMenuRecursive(routeName, subMenu)
      if (result) return true
    }
  }

  return false
}

/**
 * Check if user has access to a route based on menu permissions
 * @param route - The route object to check access for
 * @returns True if user has access, false otherwise
 */
export const hasMenuAccess = (route: Route | RouteLocationNormalized): boolean => {
  if (route.meta && !route.meta.protected) return true

  if (typeof route.name === 'string' && openRouteNames.has(route.name)) return true

  const securityStore = useSecurityStore()
  const menus = securityStore.getterMenus
  if (!menus) return false

  for (const menu of menus) {
    const result = hasMenuRecursive(route.name, menu)
    if (result) return true
  }

  return false
}

/**
 * Check if user has a specific permission
 * @param resource - The resource name
 * @param operation - The operation name
 * @returns True if user has the permission, false otherwise
 */
export const hasPermission = (resource: string, operation: string): boolean => {
  const securityStore = useSecurityStore()
  const permissions = securityStore.getterPermissions
  if (!permissions) return false

  for (const permission of permissions) {
    if (permission.resourceName === resource && permission.operationName === operation) {
      return true
    }
  }
  return false
}

/**
 * Check if user has any permissions (both permissions and menus exist)
 * @returns True if user has any permissions, false otherwise
 */
export const hasAnyPermission = (): boolean => {
  const securityStore = useSecurityStore()
  const permissions = securityStore.getterPermissions
  const menus = securityStore.getterMenus

  return !!(permissions && permissions.length > 0 && menus && menus.length > 0)
}
